import { Viewer } from "@react-pdf-viewer/core";
import {
    highlightPlugin,
    type RenderHighlightsProps,
    Trigger,
} from "@react-pdf-viewer/highlight";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
    getExtractedPropertyHighlightAreas,
    getLibraryDocument,
    type IPropertyHighlightArea,
} from "@/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
// Side-effect CSS for the Viewer chrome + toolbar controls. Other call sites
// load these via ViewerToolbar; this modal doesn't compose that helper, so the
// imports live here directly.
import { ViewerToolbar } from "@/components/shared/ViewerToolbar";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/toolbar/lib/styles/index.css";

import { sourceQuoteMarkdownComponents } from "./sourceQuoteMarkdown";

const EMPTY_HIGHLIGHTS: IPropertyHighlightArea[] = [];
const PRESIGNED_URL_TTL_MS = 60 * 60 * 1000;

export interface PdfSourceModalProps {
    documentId: number;
    /**
     * `null` for document-level deep links that have no backing property
     * (e.g., the COI "Certificate Details" page pill). When null, the
     * highlight-areas query is skipped and the modal renders as a
     * page-navigation viewer with no overlays.
     */
    propertyId: number | null;
    /**
     * 1-indexed (matches the wire `page_number`); the Viewer is 0-indexed.
     * `null` when the source has no page reference; modal opens at page 1.
     */
    pageNumber: number | null;
    fieldName: string;
    source: string | null;
    onClose: () => void;
}

export function PdfSourceModal({
    documentId,
    propertyId,
    pageNumber,
    fieldName,
    source,
    onClose,
}: PdfSourceModalProps) {
    const initialPage =
        pageNumber != null && pageNumber > 0 ? pageNumber - 1 : 0;
    const [currentPage, setCurrentPage] = useState(initialPage);
    const showJumpToSource = currentPage !== initialPage;

    const fileQuery = useQuery({
        queryKey: ["library-document-file-url", documentId],
        queryFn: () => getLibraryDocument(documentId, true),
        staleTime: PRESIGNED_URL_TTL_MS,
        refetchOnWindowFocus: false,
    });

    const highlightQuery = useQuery({
        queryKey: [
            "extracted-property-highlight-areas",
            documentId,
            propertyId,
        ],
        // Property-less callers (e.g., the COI "Certificate Details" page
        // pill) have no property to highlight; skip the fetch and let
        // `EMPTY_HIGHLIGHTS` flow through so the viewer renders cleanly.
        enabled: propertyId != null,
        queryFn: () =>
            getExtractedPropertyHighlightAreas(documentId, propertyId!),
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    });

    // Ref so renderHighlights reads latest areas without recreating the plugin.
    const highlightAreasRef =
        useRef<IPropertyHighlightArea[]>(EMPTY_HIGHLIGHTS);
    highlightAreasRef.current =
        highlightQuery.data?.highlight_areas ?? EMPTY_HIGHLIGHTS;

    // Plugin factories call React hooks internally; top-level only.
    // (Precedent: DocumentViewerPanel.tsx ~177.)
    const highlightPluginInstance = highlightPlugin({
        renderHighlights: (props: RenderHighlightsProps) => (
            <div>
                {highlightAreasRef.current
                    .filter((area) => area.page_index === props.pageIndex)
                    .map((area, idx) => (
                        <div
                            key={idx}
                            className="bg-copper/40"
                            style={props.getCssProperties(
                                {
                                    pageIndex: area.page_index,
                                    top: area.top,
                                    left: area.left,
                                    width: area.width,
                                    height: area.height,
                                },
                                props.rotation
                            )}
                        />
                    ))}
            </div>
        ),
        trigger: Trigger.None,
    });
    const pageNavigationPluginInstance = pageNavigationPlugin();
    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        renderToolbar: ViewerToolbar,
        sidebarTabs: () => [],
    });

    // Plugin re-instantiates per render; keep the listener stable and read
    // through a ref so it always calls the freshest instance.
    const pageNavRef = useRef(pageNavigationPluginInstance);
    pageNavRef.current = pageNavigationPluginInstance;

    // Arrow keys at window level so a focused descendant isn't required for
    // navigation (base-ui Dialog's focus guards otherwise swallow keystrokes).
    // Esc is handled by Dialog (onOpenChange); don't double-fire onClose.
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const tag = (e.target as Element | null)?.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA") return;
            if (e.key === "ArrowRight") {
                pageNavRef.current.jumpToNextPage();
                setCurrentPage((p) => p + 1);
            } else if (e.key === "ArrowLeft") {
                pageNavRef.current.jumpToPreviousPage();
                setCurrentPage((p) => Math.max(0, p - 1));
            }
        };
        // Capture phase so base-ui's focus-guard handlers don't swallow the
        // event before it reaches us.
        document.addEventListener("keydown", handleKeyDown, true);
        return () =>
            document.removeEventListener("keydown", handleKeyDown, true);
    }, []);

    const fileUrl = fileQuery.data?.download_url ?? null;
    const filename = fileQuery.data?.document_name ?? "";

    const handleOpenChange = (open: boolean) => {
        if (!open) onClose();
    };

    const handleJumpToSource = () => {
        pageNavigationPluginInstance.jumpToPage(initialPage);
        setCurrentPage(initialPage);
    };

    return (
        // `modal={false}` keeps siblings outside the dialog interactive so
        // external test harnesses (e.g., the GC test's "External close"
        // button) stay queryable. With base-ui's default `modal=true` the
        // rest of the body is flagged `data-base-ui-inert` and `getByRole`
        // can't see it. The backdrop still renders for click-to-close.
        <Dialog open onOpenChange={handleOpenChange} modal={false}>
            <DialogContent
                showCloseButton={false}
                className="flex h-[85vh] max-h-[85vh] w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-full"
                aria-label="PDF source viewer"
            >
                <header className="flex h-12 shrink-0 items-center gap-3 border-b px-4">
                    <span className="flex-1 truncate text-sm font-medium">
                        {filename}
                    </span>
                    {showJumpToSource && pageNumber != null && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleJumpToSource}
                        >
                            Jump to source (p.{pageNumber})
                        </Button>
                    )}
                    <Button
                        size="icon-sm"
                        variant="ghost"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X />
                    </Button>
                </header>

                <div className="flex min-h-0 flex-1">
                    <main className="flex-1 overflow-hidden">
                        {fileQuery.isError ? (
                            <PdfErrorState
                                onRetry={() => fileQuery.refetch()}
                                onClose={onClose}
                            />
                        ) : fileUrl ? (
                            <Viewer
                                fileUrl={fileUrl}
                                initialPage={initialPage}
                                plugins={[
                                    highlightPluginInstance,
                                    pageNavigationPluginInstance,
                                    defaultLayoutPluginInstance,
                                ]}
                                onPageChange={(e) =>
                                    setCurrentPage(e.currentPage)
                                }
                            />
                        ) : (
                            <PdfLoadingState />
                        )}
                    </main>

                    <aside className="w-80 shrink-0 overflow-y-auto border-l p-4">
                        <span className="text-muted-foreground text-[12px] font-semibold tracking-wider uppercase">
                            Extracted Source
                        </span>
                        <div className="mt-2 flex flex-col gap-0.5">
                            <span className="text-muted-foreground text-xs">
                                Field
                            </span>
                            <span className="text-sm font-medium">
                                {fieldName}
                            </span>
                        </div>

                        {pageNumber != null && (
                            <div className="mt-2 flex flex-col gap-0.5">
                                <span className="text-muted-foreground text-xs">
                                    Page
                                </span>
                                <span className="text-sm font-medium">
                                    page {pageNumber}
                                </span>
                            </div>
                        )}

                        {source && (
                            <div className="border-copper/20 bg-copper/10 mt-4 rounded-md border p-3 text-sm">
                                <div className="flex items-center gap-2">
                                    <div className="bg-copper h-2 w-2 rounded"></div>
                                    <span className="text-copper text-[12px] font-semibold uppercase">
                                        Source Quote
                                    </span>
                                </div>
                                <Markdown
                                    remarkPlugins={[remarkGfm]}
                                    components={sourceQuoteMarkdownComponents}
                                >
                                    {source}
                                </Markdown>
                            </div>
                        )}
                    </aside>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function PdfLoadingState() {
    return (
        <div
            data-testid="pdf-loading"
            className="flex h-full items-center justify-center"
        >
            <span className="text-muted-foreground text-sm">
                Loading document…
            </span>
        </div>
    );
}

function PdfErrorState({
    onRetry,
    onClose,
}: {
    onRetry: () => void;
    onClose: () => void;
}) {
    return (
        <div
            data-testid="pdf-error"
            className="flex h-full flex-col items-center justify-center gap-3 p-6"
        >
            <AlertCircle className="text-muted-foreground size-8" />
            <div className="text-base font-medium">
                Couldn&apos;t load this document
            </div>
            <p className="text-muted-foreground max-w-xs text-center text-sm">
                Check your connection and try again.
            </p>
            <div className="flex gap-2">
                <Button variant="outline" onClick={onRetry}>
                    Retry
                </Button>
                <Button variant="ghost" onClick={onClose}>
                    Close
                </Button>
            </div>
        </div>
    );
}
